import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';
import { callFunction } from '../lib/integrationApi';

const statusLabel = {
  active: 'Conectat',
  revoked: 'Revocat',
  neconectat: 'Neconectat',
};

const statusColor = {
  active: 'success',
  revoked: 'warning',
  neconectat: 'default',
};

export default function CoordinatorIntegrations() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState({ open: false, student: null, code: null, expiresAt: null });
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const showSnackbar = (message) => setSnackbar({ open: true, message });

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await callFunction('srbhub-links', { method: 'GET' });
      setStudents(data?.students || []);
    } catch (err) {
      showSnackbar(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filtered = useMemo(() => {
    let rows = [...students];
    if (statusFilter !== 'all') {
      rows = rows.filter((s) => (s.status || 'neconectat') === statusFilter);
    }
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      rows = rows.filter(
        (s) =>
          s.student_name?.toLowerCase().includes(term) ||
          s.email?.toLowerCase().includes(term) ||
          s.external_user_id?.toLowerCase().includes(term),
      );
    }
    return rows;
  }, [students, statusFilter, search]);

  const dialogRemaining = useMemo(() => {
    if (!dialog.expiresAt) return 0;
    return Math.max(0, Math.floor((new Date(dialog.expiresAt).getTime() - now) / 1000));
  }, [dialog.expiresAt, now]);

  const dialogCountdown = `${String(Math.floor(dialogRemaining / 60)).padStart(2, '0')}:${String(
    dialogRemaining % 60,
  ).padStart(2, '0')}`;

  const handleGenerate = async (student) => {
    setBusyId(student.id_elev);
    try {
      const data = await callFunction('srbhub-link-generate', {
        method: 'POST',
        body: { id_elev: student.id_elev },
      });
      setDialog({ open: true, student, code: data?.code ?? null, expiresAt: data?.expires_at ?? null });
      setCopied(false);
      loadData();
    } catch (err) {
      showSnackbar(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (student) => {
    if (!student.link_id) return;
    if (!window.confirm(`Revoci legătura SRBHub pentru ${student.student_name}?`)) return;
    setBusyId(student.id_elev);
    try {
      await callFunction('srbhub-link-revoke', { method: 'POST', body: { link_id: student.link_id } });
      showSnackbar('Link revocat.');
      loadData();
    } catch (err) {
      showSnackbar(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleCopy = async () => {
    if (!dialog.code) return;
    try {
      await navigator.clipboard.writeText(dialog.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showSnackbar('Copierea a eșuat.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Integrări SRBHub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Starea legăturilor și codurile de conectare pentru toți elevii.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          size="small"
          label="Caută elev"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">Toate</MenuItem>
            <MenuItem value="active">Conectați</MenuItem>
            <MenuItem value="revoked">Revocați</MenuItem>
            <MenuItem value="neconectat">Neconectați</MenuItem>
          </Select>
        </FormControl>
        <Button onClick={loadData} startIcon={<RefreshIcon />}>
          Reîncarcă
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {filtered.length} din {students.length} elevi
      </Alert>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Elev</TableCell>
              <TableCell>Clasă</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>ID SRBHub</TableCell>
              <TableCell>Cod activ</TableCell>
              <TableCell align="center">Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((student) => (
              <TableRow key={student.id_elev} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {student.student_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {student.email}
                  </Typography>
                </TableCell>
                <TableCell>{student.clasa || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabel[student.status] || student.status}
                    color={statusColor[student.status] || 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {student.external_user_id || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" fontFamily="monospace">
                    {student.active_code || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    startIcon={<KeyIcon />}
                    onClick={() => handleGenerate(student)}
                    disabled={busyId === student.id_elev}
                  >
                    Cod
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<LinkOffIcon />}
                    onClick={() => handleRevoke(student)}
                    disabled={busyId === student.id_elev || !student.link_id || student.status !== 'active'}
                  >
                    Revocă
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Niciun elev pentru filtrele selectate.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={() => setDialog((d) => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Cod de legătură</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {dialog.student?.student_name}
          </Typography>
          <Box
            sx={{
              mt: 2,
              px: 3,
              py: 2,
              borderRadius: 2,
              bgcolor: '#f5f3ff',
              border: '1px dashed',
              borderColor: 'primary.main',
              textAlign: 'center',
            }}
          >
            <Typography variant="h4" fontFamily="monospace" fontWeight="bold" letterSpacing={4}>
              {dialog.code}
            </Typography>
            <Typography variant="caption" color={dialogRemaining > 0 ? 'text.secondary' : 'error'}>
              {dialogRemaining > 0 ? `Expiră în ${dialogCountdown}` : 'Cod expirat'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopy} startIcon={<CopyIcon />} disabled={!dialog.code || dialogRemaining === 0}>
            {copied ? 'Copiat!' : 'Copiază'}
          </Button>
          <Button onClick={() => setDialog((d) => ({ ...d, open: false }))}>Închide</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Container>
  );
}
